import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("oc_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("oc_token");
      localStorage.removeItem("oc_merchant");
      if (!window.location.pathname.startsWith("/login") &&
          !window.location.pathname.startsWith("/signup") &&
          !window.location.pathname.startsWith("/qr/") &&
          window.location.pathname !== "/" &&
          !window.location.pathname.startsWith("/pricing")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export const auth = {
  signup: (data) => api.post("/auth/signup", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateOnboarding: (data) => api.patch("/auth/onboarding", data).then((r) => r.data),
};

export const dashboard = {
  stats: () => api.get("/dashboard/stats").then((r) => r.data),
  revenueTrend: () => api.get("/dashboard/revenue-trend").then((r) => r.data),
  dailyRec: () => api.get("/ai/daily-recommendation").then((r) => r.data),
};

export const qrCampaigns = {
  list: () => api.get("/qr-campaigns").then((r) => r.data),
  get: (id) => api.get(`/qr-campaigns/${id}`).then((r) => r.data),
  create: (data) => api.post("/qr-campaigns", data).then((r) => r.data),
  remove: (id) => api.delete(`/qr-campaigns/${id}`).then((r) => r.data),
};

export const publicApi = {
  getCampaign: (id) => axios.get(`${API}/public/campaign/${id}`).then((r) => r.data),
  optin: (id, data) => axios.post(`${API}/public/campaign/${id}/optin`, data).then((r) => r.data),
};

export const customers = {
  list: (params) => api.get("/customers", { params }).then((r) => r.data),
  get: (id) => api.get(`/customers/${id}`).then((r) => r.data),
  create: (data) => api.post("/customers", data).then((r) => r.data),
  update: (id, data) => api.patch(`/customers/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/customers/${id}`).then((r) => r.data),
};

export const products = {
  list: () => api.get("/products").then((r) => r.data),
  create: (data) => api.post("/products", data).then((r) => r.data),
  update: (id, data) => api.patch(`/products/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/products/${id}`).then((r) => r.data),
};

export const rewards = {
  list: () => api.get("/rewards").then((r) => r.data),
  create: (data) => api.post("/rewards", data).then((r) => r.data),
  use: (id) => api.post(`/rewards/${id}/use`).then((r) => r.data),
};

export const waCampaigns = {
  list: () => api.get("/wa-campaigns").then((r) => r.data),
  create: (data) => api.post("/wa-campaigns", data).then((r) => r.data),
  update: (id, data) => api.patch(`/wa-campaigns/${id}`, data).then((r) => r.data),
  send: (id) => api.post(`/wa-campaigns/${id}/send`).then((r) => r.data),
  remove: (id) => api.delete(`/wa-campaigns/${id}`).then((r) => r.data),
};

export const orders = {
  list: () => api.get("/orders").then((r) => r.data),
  create: (data) => api.post("/orders", data).then((r) => r.data),
  importCsv: (file, source) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source", source);
    return api.post("/orders/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  importPreview: (source) => api.get(`/orders/import-preview?source=${source}`).then((r) => r.data),
};

export const repeatOrder = {
  candidates: () => api.get("/repeat-order/candidates").then((r) => r.data),
};

export const referrals = {
  leaderboard: () => api.get("/referrals/leaderboard").then((r) => r.data),
};

export const membership = {
  stats: () => api.get("/membership/stats").then((r) => r.data),
};

export const resellers = {
  list: () => api.get("/resellers").then((r) => r.data),
  create: (data) => api.post("/resellers", data).then((r) => r.data),
  update: (id, data) => api.patch(`/resellers/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/resellers/${id}`).then((r) => r.data),
};

export const profit = {
  summary: () => api.get("/profit/summary").then((r) => r.data),
};

export const sku = {
  analytics: () => api.get("/sku/analytics").then((r) => r.data),
};

export const benchmark = {
  get: () => api.get("/benchmark").then((r) => r.data),
};

export const ai = {
  generate: (data) => api.post("/ai/generate", data).then((r) => r.data),
  segmentation: () => api.post("/ai/segmentation").then((r) => r.data),
};

export const staff = {
  list: () => api.get("/staff").then((r) => r.data),
  create: (data) => api.post("/staff", data).then((r) => r.data),
  update: (id, data) => api.patch(`/staff/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/staff/${id}`).then((r) => r.data),
  login: (data) => api.post("/auth/staff-login", data).then((r) => r.data),
};

export const integrations = {
  getWA: () => api.get("/integrations/wa").then((r) => r.data),
  updateWA: (data) => api.patch("/integrations/wa", data).then((r) => r.data),
};

export const payouts = {
  list: (resellerId) => api.get(`/resellers/${resellerId}/payouts`).then((r) => r.data),
  create: (resellerId, data) => api.post(`/resellers/${resellerId}/payouts`, data).then((r) => r.data),
};

export const cms = {
  getLanding: () => api.get("/public/cms/landing").then((r) => r.data),
  getLandingFull: () => api.get("/public/cms/landing-full").then((r) => r.data),
};

export const billing = {
  packages: () => api.get("/billing/packages").then((r) => r.data),
  gateways: () => api.get("/billing/gateways").then((r) => r.data),
  checkout: (packageId, originUrl) => api.post("/billing/checkout", { package_id: packageId, origin_url: originUrl }).then((r) => r.data),
  status: (sessionId) => api.get(`/billing/status/${sessionId}`).then((r) => r.data),
  xenditInvoice: (packageId, originUrl) => api.post("/billing/xendit/create-invoice", { package_id: packageId, origin_url: originUrl }).then((r) => r.data),
  xenditStatus: (externalId) => api.get(`/billing/xendit/status/${externalId}`).then((r) => r.data),
  midtransTx: (packageId) => api.post("/billing/midtrans/create-transaction", { package_id: packageId }).then((r) => r.data),
  midtransStatus: (orderId) => api.get(`/billing/midtrans/status/${orderId}`).then((r) => r.data),
};

export const waIntegration = {
  update: (data) => api.patch("/integrations/wa-v2", data).then((r) => r.data),
};
